import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams } from "react-router"
import api from "../../lib/api"
import { toastError, toastSuccess } from "../../lib/toast"
import ModuleLayout from "../../components/admin/ModuleLayout"
import DataTable, { type DataTableColumn } from "../../components/ui/table/DataTable"
import { btnPrimary, btnSecondary } from "../../components/admin/FormComponents"

// Reevaluación de intentos — openspec/changes/section-quiz-jev-grading
// (spec section-quiz-regrading). Dos pasos sin modales: vista previa (no
// escribe notas) y luego aplicar solo lo elegido.

type ItemState = "changed" | "unchanged" | "excluded_override" | "not_evaluable"

type RegradeItem = {
  id: string
  attempt_id: string
  group_batch_id: string | null
  student_name: string
  student_email: string | null
  state: ItemState
  before_score: number
  before_passed: boolean
  overridden: boolean
  after_score: number | null
  after_passed: boolean | null
  passing_score_used: number
  passing_score_source: "attempt" | "quiz_current"
  crosses_threshold: boolean
  warnings: string[]
  error: string | null
  applied: boolean
  forced_over_override: boolean
}

type RunSummary = {
  id: string
  grader: string
  status: "previewed" | "applied"
  created_at: string
  created_by: string | null
  applied_at: string | null
  applied_by: string | null
  item_count: number
  applied_count: number
}

type ApplyResultCode = "applied" | "unchanged" | "stale" | "skipped_override" | "not_evaluable" | "not_found" | "already_applied"

const BATCH_SIZE = 10
const PAGE_SIZE = 20

const RESULT_LABEL: Record<ApplyResultCode, string> = {
  applied: "aplicados",
  unchanged: "sin cambio",
  stale: "desactualizados (el intento cambió después de la vista previa)",
  skipped_override: "omitidos por override manual",
  not_evaluable: "no evaluables",
  not_found: "no encontrados",
  already_applied: "ya aplicados antes",
}

function PassMark({ passed }: { passed: boolean | null }) {
  if (passed === null) return <span className="text-gray-400">—</span>
  return passed
    ? <span className="font-medium text-green-600">✓</span>
    : <span className="font-medium text-error-600 dark:text-error-400">✗</span>
}

function Badge({ children, tone }: { children: React.ReactNode; tone: "amber" | "blue" | "gray" }) {
  const cls = {
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    gray: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  }[tone]
  return <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${cls}`}>{children}</span>
}

function StudentCell({ item }: { item: RegradeItem }) {
  return (
    <div>
      <p className="font-medium text-gray-900 dark:text-white">{item.student_name}</p>
      {item.student_email && <p className="text-xs text-gray-500 dark:text-gray-400">{item.student_email}</p>}
    </div>
  )
}

function ScoreChange({ item }: { item: RegradeItem }) {
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <span>{item.before_score}</span><PassMark passed={item.before_passed} />
      <span className="text-gray-400">→</span>
      <span>{item.after_score ?? "—"}</span><PassMark passed={item.after_passed} />
    </div>
  )
}

function Notes({ item }: { item: RegradeItem }) {
  return (
    <div className="flex flex-wrap gap-1">
      {item.crosses_threshold && <Badge tone="blue">Cruza el umbral</Badge>}
      {item.warnings.includes("enrollment_completed") && (
        <span title="El curso de este estudiante ya está completado. Al aplicar, la inscripción y el certificado NO se revocan.">
          <Badge tone="amber">Curso ya completado</Badge>
        </span>
      )}
      {item.warnings.includes("current_threshold") && (
        <span title={`Este intento no guardó su umbral original; se usó el actual del examen (${item.passing_score_used}).`}>
          <Badge tone="gray">Umbral actual</Badge>
        </span>
      )}
      {item.applied && <Badge tone="gray">Aplicado{item.forced_over_override ? " (sobre override)" : ""}</Badge>}
    </div>
  )
}

function SectionBlock({ title, description, count, children }: { title: string; description?: string; count: number; children: React.ReactNode }) {
  if (count === 0) return null
  return (
    <section className="mt-8">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title} <span className="font-normal text-gray-500">({count})</span></h2>
      {description && <p className="mb-3 mt-0.5 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
      {children}
    </section>
  )
}

function PagedTable({ columns, rows }: { columns: DataTableColumn<RegradeItem>[]; rows: RegradeItem[] }) {
  const [page, setPage] = useState(1)
  return (
    <DataTable
      columns={columns}
      rows={rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)}
      rowKey={(i) => i.attempt_id}
      loading={false}
      emptyMessage="Sin intentos."
      sort={null}
      onSortChange={() => {}}
      page={page}
      pageSize={PAGE_SIZE}
      total={rows.length}
      onPageChange={setPage}
    />
  )
}

export default function SectionQuizRegradePage() {
  const { sectionQuizId } = useParams<{ sectionQuizId: string }>()
  const navigate = useNavigate()

  const [items, setItems] = useState<RegradeItem[]>([])
  const [runId, setRunId] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [applying, setApplying] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [forced, setForced] = useState<Set<string>>(new Set())
  const [history, setHistory] = useState<RunSummary[]>([])
  const [applySummary, setApplySummary] = useState<string | null>(null)

  const loadHistory = useCallback(async () => {
    if (!sectionQuizId) return
    try {
      const res = await api.get(`/academy/section-quizzes/${sectionQuizId}/regrade/runs`)
      setHistory(res.data.data ?? [])
    } catch (err) {
      toastError(err)
    }
  }, [sectionQuizId])

  useEffect(() => { void loadHistory() }, [loadHistory])

  const loadRun = useCallback(async (id: string, preselect: boolean) => {
    const res = await api.get(`/academy/section-quizzes/regrade/runs/${id}`)
    const loaded: RegradeItem[] = res.data.data?.items ?? []
    setItems(loaded)
    if (preselect) {
      setSelected(new Set(loaded.filter((i) => i.state === "changed" && !i.applied).map((i) => i.attempt_id)))
      setForced(new Set())
    }
  }, [])

  const startPreview = async () => {
    if (!sectionQuizId || progress) return
    setApplySummary(null)
    setItems([])
    try {
      const created = await api.post(`/academy/section-quizzes/${sectionQuizId}/regrade/runs`, { grader: "llama" })
      const id: string = created.data.data.run_id
      const attemptIds: string[] = created.data.data.attempt_ids
      setRunId(id)
      setProgress({ done: 0, total: attemptIds.length })
      for (let i = 0; i < attemptIds.length; i += BATCH_SIZE) {
        await api.post(`/academy/section-quizzes/regrade/runs/${id}/preview`, { attempt_ids: attemptIds.slice(i, i + BATCH_SIZE) })
        setProgress({ done: Math.min(i + BATCH_SIZE, attemptIds.length), total: attemptIds.length })
      }
      await loadRun(id, true)
      void loadHistory()
    } catch (err) {
      toastError(err)
    } finally {
      setProgress(null)
    }
  }

  const apply = async () => {
    if (!runId || applying) return
    const attemptIds = [...selected, ...forced]
    if (attemptIds.length === 0) return
    setApplying(true)
    try {
      const res = await api.post(`/academy/section-quizzes/regrade/runs/${runId}/apply`, {
        attempt_ids: attemptIds,
        force_override_attempt_ids: [...forced],
      })
      const results: { attempt_id: string; result: ApplyResultCode }[] = res.data.data?.results ?? []
      const counts = new Map<ApplyResultCode, number>()
      for (const r of results) counts.set(r.result, (counts.get(r.result) ?? 0) + 1)
      setApplySummary([...counts].map(([code, n]) => `${n} ${RESULT_LABEL[code]}`).join(" · "))
      toastSuccess(`Reevaluación aplicada: ${counts.get("applied") ?? 0} intento(s)`)
      await loadRun(runId, true)
      void loadHistory()
    } catch (err) {
      toastError(err)
    } finally {
      setApplying(false)
    }
  }

  const changed = useMemo(() => items.filter((i) => i.state === "changed"), [items])
  const unchanged = useMemo(() => items.filter((i) => i.state === "unchanged"), [items])
  const excluded = useMemo(() => items.filter((i) => i.state === "excluded_override"), [items])
  const notEvaluable = useMemo(() => items.filter((i) => i.state === "not_evaluable"), [items])
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id); else next.add(id)
    setter(next)
  }

  const selectColumn = (set: Set<string>, setter: (s: Set<string>) => void, ariaLabel: string): DataTableColumn<RegradeItem> => ({
    key: "select",
    label: "",
    headerAriaLabel: ariaLabel,
    render: (i) => (
      <input
        type="checkbox"
        aria-label={`${ariaLabel}: ${i.student_name}`}
        checked={set.has(i.attempt_id)}
        disabled={i.applied}
        onChange={() => toggle(set, setter, i.attempt_id)}
      />
    ),
  })

  const changedColumns: DataTableColumn<RegradeItem>[] = [
    selectColumn(selected, setSelected, "Aplicar"),
    { key: "student", label: "Estudiante(s)", render: (i) => <StudentCell item={i} /> },
    { key: "change", label: "Antes → Después", render: (i) => <ScoreChange item={i} /> },
    { key: "notes", label: "Notas", render: (i) => <Notes item={i} /> },
  ]
  const excludedColumns: DataTableColumn<RegradeItem>[] = [
    selectColumn(forced, setForced, "Incluir"),
    { key: "student", label: "Estudiante(s)", render: (i) => <StudentCell item={i} /> },
    { key: "change", label: "Antes → Después", render: (i) => <ScoreChange item={i} /> },
    { key: "notes", label: "Notas", render: (i) => <Notes item={i} /> },
  ]
  const plainColumns: DataTableColumn<RegradeItem>[] = [
    { key: "student", label: "Estudiante(s)", render: (i) => <StudentCell item={i} /> },
    { key: "change", label: "Antes → Después", render: (i) => <ScoreChange item={i} /> },
    { key: "notes", label: "Notas", render: (i) => <Notes item={i} /> },
  ]
  const errorColumns: DataTableColumn<RegradeItem>[] = [
    { key: "student", label: "Estudiante(s)", render: (i) => <StudentCell item={i} /> },
    { key: "error", label: "Motivo", render: (i) => <span className="text-sm text-gray-600 dark:text-gray-300">{i.error}</span> },
  ]

  const pending = selected.size + forced.size
  const crossing = changed.filter((i) => i.crosses_threshold).length

  return (
    <ModuleLayout
      breadcrumbs={[{ label: "SateLab", to: "/" }, "Contenido", { label: "Exámenes IA", to: "/instructor/section-quizzes" }, { label: "Intentos", to: `/instructor/section-quizzes/${sectionQuizId}/attempts` }, "Reevaluar"]}
      title="Reevaluar intentos"
      subtitle="Calcula de nuevo la nota de los intentos ya enviados con el calificador actual. La vista previa no cambia ninguna nota."
      actions={<button type="button" className={btnSecondary} onClick={() => navigate(-1)}>Volver</button>}
      tabs={[]}
      metrics={items.length > 0 ? [
        { label: "Evaluados", value: String(items.length) },
        { label: "Cambian", value: String(changed.length) },
        { label: "Cruzan el umbral", value: String(crossing), tone: crossing > 0 ? "warning" : "good" },
        { label: "Excluidos por override", value: String(excluded.length) },
      ] : []}
    >
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className={btnPrimary} onClick={() => void startPreview()} disabled={progress !== null}>
          {progress ? "Calculando…" : items.length > 0 ? "Volver a calcular vista previa" : "Calcular vista previa"}
        </button>
        {progress && (
          <p className="text-sm text-gray-600 dark:text-gray-300" role="status">
            Procesando {progress.done} de {progress.total} intentos
          </p>
        )}
      </div>

      {applySummary && (
        <p className="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-700 dark:bg-gray-800/60 dark:text-gray-200" role="status">
          Resultado: {applySummary}
        </p>
      )}

      {items.length > 0 && !progress && (
        <>
          <SectionBlock
            title="Cambian"
            description="Intentos cuyo resultado cambia. Revisa los que cruzan el umbral antes de aplicar: un aprobado que pasa a reprobado conserva su inscripción y certificado."
            count={changed.length}
          >
            <PagedTable columns={changedColumns} rows={changed} />
          </SectionBlock>

          <SectionBlock
            title="Excluidos por override manual"
            description="Un instructor fijó estos resultados a mano. No se tocan salvo que los incluyas explícitamente; hacerlo reemplaza el override."
            count={excluded.length}
          >
            <PagedTable columns={excludedColumns} rows={excluded} />
          </SectionBlock>

          <SectionBlock
            title="No evaluables"
            description="El calificador falló en estos intentos. No se modifican; vuelve a calcular la vista previa para reintentar."
            count={notEvaluable.length}
          >
            <PagedTable columns={errorColumns} rows={notEvaluable} />
          </SectionBlock>

          <SectionBlock title="Sin cambio" count={unchanged.length}>
            <PagedTable columns={plainColumns} rows={unchanged} />
          </SectionBlock>

          <div className="sticky bottom-0 mt-8 flex items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur dark:border-gray-800 dark:bg-gray-900/90">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {pending === 0 ? "No hay intentos seleccionados." : `${pending} intento(s) seleccionado(s) para aplicar.`}
            </p>
            <button type="button" className={btnPrimary} onClick={() => void apply()} disabled={pending === 0 || applying}>
              {applying ? "Aplicando…" : "Aplicar seleccionados"}
            </button>
          </div>
        </>
      )}

      <SectionBlock title="Historial de reevaluaciones" count={history.length}>
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {history.map((r) => (
            <li key={r.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2.5 text-sm">
              <span className="text-gray-900 dark:text-white">
                {new Date(r.created_at).toLocaleString("es-CO")}
                <span className="ml-2 text-gray-500">{r.created_by ?? "—"} · {r.grader}</span>
              </span>
              <span className="text-gray-500 dark:text-gray-400">
                {r.status === "applied"
                  ? `Aplicada${r.applied_by ? ` por ${r.applied_by}` : ""} · ${r.applied_count} de ${r.item_count}`
                  : `Solo vista previa · ${r.item_count} intentos`}
              </span>
            </li>
          ))}
        </ul>
      </SectionBlock>
    </ModuleLayout>
  )
}
